import _ from 'lodash'
import { store } from '@atredux/store'
import { getRoles as getStoredRoles } from '@atredux/selectors/atRoleSelector'
import { setRoles } from '@atredux/slices/atRoleSlice'

export const AssociateToolsRoleServiceConstants = Object.freeze({
    // Roles that prevent the DEFAULT_ROLE from being added
    NON_DEFAULT_ROLES: Object.freeze(['vendor']),

    // Default role for users that don't have a NON_DEFAULT_ROLE.
    // Naming like this to prevent potential name clashing later
    DEFAULT_ROLE: '__default__',
})

/**
 * Initializes the service with the input roles (can only be called once, if called multiple times, it will
 * log an error and ignore the request). This function may also add the {@link AssociateToolsRoleServiceConstants.DEFAULT_ROLE}
 * if the roles received are not included in the {@link AssociateToolsRoleServiceConstants.NON_DEFAULT_ROLES}.
 *
 * @param inputRoles: string[]
 */
export const init = (inputRoles: string[]) => {
    const roles = getStoredRoles(store.getState())
    if (roles) {
        console.error('ATBase: associateToolsRoleService: Roles already initialized')
        return
    }

    console.debug('associateToolsRoleService: got the following as inputRoles', inputRoles)
    if (!_.isArray(inputRoles)) {
        console.error('ATBase: associateToolsRoleService: Expected inputRoles to be an array', inputRoles)
        return
    }

    const rolesCopy = _.clone(inputRoles)
    if (!_includesNonDefaultRole(rolesCopy)) {
        console.debug('associateToolsRoleService: No NON_DEFAULT_ROLES found in ', rolesCopy, 'adding default role')
        rolesCopy.push(AssociateToolsRoleServiceConstants.DEFAULT_ROLE)
    }

    store.dispatch(setRoles({ roles: rolesCopy }))
    console.debug('associateToolsRoleService: Initialized roles', rolesCopy)
}

/**
 * Returns a copy of the roles. Will log an error if this service hasn't been initialized yet.
 *
 * @return {Array} - Array of roles (i.e. ['vendor', 'openid'])
 */
export const getRoles = () => {
    const roles = getStoredRoles(store.getState())
    if (roles === undefined) {
        console.warn('associateToolsRoleService: roles not defined yet')
        return
    }
    return _.clone(roles)
}

/**
 * Determines if a particular role is included in the roles for the user. Will log an error if this
 * service hasn't been initialized yet and will return false.
 *
 * @param {string} role - The role to check.
 * @return {boolean} - True if the role is present, false if it isn't (or if this service hasn't been initialized).
 */
export const hasRole = (role: string) => {
    const roles = getStoredRoles(store.getState())
    if (roles === undefined) {
        console.warn('associateToolsRoleService: roles not defined yet')
        return false
    }

    return roles.find((r: string) => r === role)
}

/**
 * Determines if at least one role from an array is included in the roles for the user.
 *
 * @return {boolean} - True if at least one role is present, false if it isn't (or if this service hasn't been initialized).
 * @param inputRoles: string[]
 */
export const containsAtLeastOne = (inputRoles: string[]) => {
    const roles = getStoredRoles(store.getState())
    if (roles === undefined) {
        console.warn('associateToolsRoleService: roles not defined yet')
        return false
    }

    if (!inputRoles) {
        console.warn('associateToolsRoleService: inputRoles invalid value `{}`', inputRoles)
        return false
    }

    return inputRoles.filter((role) => roles.includes(role)).length > 0
}

/**
 * Function to determine if the inputRoles contains any of the {@link AssociateToolsRoleServiceConstants.NON_DEFAULT_ROLES}.
 *
 * @param {Array} inputRoles - Array of strings representing roles.
 * @return {boolean} - True if the inputRoles include any of the {@link AssociateToolsRoleServiceConstants.NON_DEFAULT_ROLES}.
 */
const _includesNonDefaultRole = (inputRoles: string[]) => {
    console.debug('associateToolsRoleService: Checking roles', inputRoles, 'against', AssociateToolsRoleServiceConstants.NON_DEFAULT_ROLES)
    return _.some(inputRoles, (role: string) => {
        return AssociateToolsRoleServiceConstants.NON_DEFAULT_ROLES.indexOf(role) !== -1
    })
}
